import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { useDocumentMove, DOCUMENT_DRAG_MIME } from "./documentMoveContext";
import { DocumentMoveDestination, validateDocumentMove } from "./documentMoveValidation";

export type SubmissionOutlineStatus =
  | "NA"
  | "Valid"
  | "Verified"
  | "Unverifiable"
  | "No Evidence of Modification"
  | "Verification Required";

export type SubmissionOutlineItem = {
  id: string;
  label: string;
  status: SubmissionOutlineStatus;
  anchorId: string;
  hasNew?: boolean;
  showStatus?: boolean;
  children?: SubmissionOutlineItem[];
  moveDestination?: DocumentMoveDestination;
  idTypeTargets?: DocumentMoveDestination[];
  disableDuringMove?: boolean;
};

type SubmissionOutlineNavigationProps = {
  items: SubmissionOutlineItem[];
  loading?: boolean;
  propertyName?: string | null;
  appId?: string | number | null;
  applicantName?: string | null;
  applicantType?: string | null;
  canNavigatePrevTab?: boolean;
  canNavigateNextTab?: boolean;
  onNavigatePrevTab?: (() => void) | null;
  onNavigateNextTab?: (() => void) | null;
  deletedDocCount?: number;
  onOpenDeletedDocs?: (() => void) | null;
  canUseBulkDocumentActions?: boolean;
  applicationStatus?: SubmissionOutlineStatus;
  applicationStatusTooltip?: string | null;
  contentRootId?: string | null;
  disableNavigationInMoveMode?: boolean;
};

const OUTLINE_SCROLL_TOP_OFFSET = 96;

// A drag in progress suppresses normal wheel/trackpad scrolling, so any
// destination below the fold (typically Other Documents) is unreachable once
// the user has picked a document up. Holding the pointer near the outline's
// top or bottom edge scrolls it instead, ramping up the closer to the edge the
// pointer gets.
const OUTLINE_DRAG_SCROLL_ZONE_PX = 72;
const OUTLINE_DRAG_SCROLL_MAX_SPEED_PX = 16;
const OUTLINE_DRAG_SCROLL_MIN_SPEED_PX = 2;

const flattenOutlineItems = (items: SubmissionOutlineItem[]) =>
  items.flatMap((item) => [item, ...(item.children || [])]);

export const scrollOutlineTargetIntoView = (
  target: HTMLElement,
  contentRoot?: HTMLElement | null
) => {
  const targetRect = target.getBoundingClientRect();
  const rootCanScroll =
    contentRoot &&
    contentRoot.scrollHeight > contentRoot.clientHeight &&
    ["auto", "scroll"].includes(window.getComputedStyle(contentRoot).overflowY);

  if (rootCanScroll) {
    const rootRect = contentRoot.getBoundingClientRect();
    contentRoot.scrollTo({
      top:
        contentRoot.scrollTop +
        targetRect.top -
        rootRect.top -
        OUTLINE_SCROLL_TOP_OFFSET,
      behavior: "smooth",
    });
    return;
  }

  window.scrollTo({
    top: window.scrollY + targetRect.top - OUTLINE_SCROLL_TOP_OFFSET,
    behavior: "smooth",
  });
};

const SubmissionOutlineNavigation = ({
  items,
  loading = false,
  propertyName,
  appId,
  applicantName,
  applicantType,
  canNavigatePrevTab = false,
  canNavigateNextTab = false,
  onNavigatePrevTab,
  onNavigateNextTab,
  deletedDocCount = 0,
  onOpenDeletedDocs,
  canUseBulkDocumentActions = true,
  applicationStatus = "NA",
  applicationStatusTooltip,
  contentRootId,
  disableNavigationInMoveMode = false,
}: SubmissionOutlineNavigationProps) => {
  const [activeItemId, setActiveItemId] = React.useState("");
  const [dragOverDestinationId, setDragOverDestinationId] = React.useState("");
  const outlineRef = React.useRef<HTMLElement | null>(null);
  const dragScrollRef = React.useRef({ direction: 0, speed: 0, frame: 0 });
  const documentMove = useDocumentMove();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardPath = location.pathname.startsWith("/landloard") ? "/landloard" : "/ats";
  const flatItems = React.useMemo(() => flattenOutlineItems(items), [items]);

  React.useEffect(() => {
    if (flatItems.length === 0) {
      setActiveItemId("");
      return;
    }
    if (!flatItems.some((item) => item.id === activeItemId)) {
      setActiveItemId(flatItems[0].id);
    }
  }, [activeItemId, flatItems]);

  React.useEffect(() => {
    if (flatItems.length === 0) {
      return undefined;
    }
    if (typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const contentRoot = contentRootId
      ? document.getElementById(contentRootId)
      : document;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visibleEntry?.target?.id) return;

        const visibleItem = flatItems.find(
          (item) => item.anchorId === visibleEntry.target.id
        );
        if (visibleItem) setActiveItemId(visibleItem.id);
      },
      {
        rootMargin: "-120px 0px -55% 0px",
        threshold: [0.1, 0.25, 0.5, 0.75],
      }
    );

    flatItems.forEach((item) => {
      const element =
        contentRoot instanceof HTMLElement
          ? contentRoot.querySelector<HTMLElement>(`#${item.anchorId}`)
          : document.getElementById(item.anchorId);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [contentRootId, flatItems]);

  // Tracked on document rather than on the outline itself: the pointer can
  // leave the sidebar mid-drag, and a listener bound to the sidebar would stop
  // firing without ever telling us to halt the scroll.
  React.useEffect(() => {
    const scrollState = dragScrollRef.current;

    const stopScrolling = () => {
      if (scrollState.frame) cancelAnimationFrame(scrollState.frame);
      scrollState.frame = 0;
      scrollState.direction = 0;
      scrollState.speed = 0;
    };

    const step = () => {
      const outline = outlineRef.current;
      if (!outline || !scrollState.direction) {
        stopScrolling();
        return;
      }
      outline.scrollTop += scrollState.direction * scrollState.speed;
      scrollState.frame = requestAnimationFrame(step);
    };

    const handleDragOver = (event: DragEvent) => {
      const outline = outlineRef.current;
      if (!outline) return;
      // Only our own document drags scroll the outline; dragging selected text
      // or a desktop file past the sidebar should leave it alone.
      if (!event.dataTransfer?.types?.includes(DOCUMENT_DRAG_MIME)) return;

      const rect = outline.getBoundingClientRect();
      const isOverOutline =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!isOverOutline) {
        stopScrolling();
        return;
      }

      const distanceToTop = event.clientY - rect.top;
      const distanceToBottom = rect.bottom - event.clientY;
      let direction = 0;
      let depthIntoZone = 0;
      if (distanceToTop < OUTLINE_DRAG_SCROLL_ZONE_PX) {
        direction = -1;
        depthIntoZone = OUTLINE_DRAG_SCROLL_ZONE_PX - distanceToTop;
      } else if (distanceToBottom < OUTLINE_DRAG_SCROLL_ZONE_PX) {
        direction = 1;
        depthIntoZone = OUTLINE_DRAG_SCROLL_ZONE_PX - distanceToBottom;
      }

      if (!direction) {
        stopScrolling();
        return;
      }

      scrollState.direction = direction;
      scrollState.speed = Math.max(
        OUTLINE_DRAG_SCROLL_MIN_SPEED_PX,
        Math.ceil(
          (depthIntoZone / OUTLINE_DRAG_SCROLL_ZONE_PX) *
            OUTLINE_DRAG_SCROLL_MAX_SPEED_PX
        )
      );
      if (!scrollState.frame) {
        scrollState.frame = requestAnimationFrame(step);
      }
    };

    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("drop", stopScrolling);
    document.addEventListener("dragend", stopScrolling);
    return () => {
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("drop", stopScrolling);
      document.removeEventListener("dragend", stopScrolling);
      stopScrolling();
    };
  }, []);

  const handleNavigate = (item: SubmissionOutlineItem) => {
    const isChoosingMoveDestination =
      (documentMove.isMoveMode || documentMove.activeDocuments.length > 0) &&
      !!item.moveDestination;
    if (isChoosingMoveDestination) {
      void documentMove.moveSelectionToDestination(item.moveDestination!);
      return;
    }
    if (disableNavigationInMoveMode && (documentMove.isMoveMode || documentMove.isMoving)) return;
    setActiveItemId(item.id);
    const contentRoot = contentRootId
      ? document.getElementById(contentRootId)
      : null;
    const target =
      contentRoot?.querySelector<HTMLElement>(`#${item.anchorId}`) ||
      document.getElementById(item.anchorId);
    if (!target) return;

    scrollOutlineTargetIntoView(target, contentRoot);
  };

  const canAcceptDraggedDocuments = (
    destination: DocumentMoveDestination | undefined
  ) =>
    Boolean(destination) &&
    documentMove.activeDocuments.length > 0 &&
    validateDocumentMove(documentMove.activeDocuments, destination!).valid;

  const getStatusIcon = (status: SubmissionOutlineStatus) => {
    if (status === "Valid" || status === "Verified") {
      return <CheckIcon fontSize="small" />;
    }
    if (status === "Verification Required") {
      return <WarningIcon fontSize="small" />;
    }
    if (status === "No Evidence of Modification") {
      return <InfoOutlinedIcon fontSize="small" />;
    }
    if (status === "Unverifiable") {
      return <ErrorIcon fontSize="small" />;
    }
    return null;
  };

  const renderItem = (
    item: SubmissionOutlineItem,
    depth = 0,
    topLevelIndex?: number
  ) => {
    const isActive = activeItemId === item.id;
    const indent = depth * 18;
    const showStatus = item.showStatus !== false;
    const topLevelSpacing =
      depth === 0 && typeof topLevelIndex === "number" && topLevelIndex > 0
        ? "8px"
        : undefined;
    const hasMoveSelection = documentMove.isMoveMode || documentMove.activeDocuments.length > 0;
    const isMoveTarget = hasMoveSelection && !!item.moveDestination;
    const moveValidation = isMoveTarget
      ? validateDocumentMove(documentMove.activeDocuments, item.moveDestination!)
      : { valid: false, message: undefined };
    const isInvalidMoveTarget = (isMoveTarget && item.disableDuringMove) || (isMoveTarget && !moveValidation.valid);
    const isNonTargetDuringMoveSelection = hasMoveSelection && !item.moveDestination;
    const isMoveTargetDisabled = isInvalidMoveTarget || isNonTargetDuringMoveSelection;
    // Outline items only celebrate on a successful move (delete celebrates the
    // trash button, id "trash"), so a celebrating item always shows the truck.
    const isCelebrating =
      Boolean(documentMove.celebrationId) &&
      (documentMove.celebrationId === item.id ||
        documentMove.celebrationId === item.moveDestination?.id);
    return (
      <React.Fragment key={item.id}>
        <button
          type="button"
          className={`submission-outline-item ${
            showStatus
              ? `submission-outline-item-${item.status
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`
              : "submission-outline-item-neutral"
          } ${isActive ? "active" : ""} ${isMoveTarget && !isInvalidMoveTarget ? "submission-outline-move-target" : ""} ${isMoveTargetDisabled ? "submission-outline-move-target-disabled" : ""} ${dragOverDestinationId === item.moveDestination?.id ? "drag-over" : ""} ${isCelebrating ? "submission-outline-celebrate" : ""}`}
          onClick={() => handleNavigate(item)}
          onDragOver={(event) => {
            // The MIME marker identifies our document drag; validation keeps
            // unavailable or full destinations from advertising themselves as
            // browser drop targets.
            if (
              !item.moveDestination ||
              !canAcceptDraggedDocuments(item.moveDestination) ||
              !event.dataTransfer.types.includes(DOCUMENT_DRAG_MIME)
            )
              return;
            event.preventDefault();
            setDragOverDestinationId(item.moveDestination.id);
          }}
          onDragLeave={() => setDragOverDestinationId("")}
          onDrop={(event) => {
            if (
              !item.moveDestination ||
              !canAcceptDraggedDocuments(item.moveDestination) ||
              !event.dataTransfer.types.includes(DOCUMENT_DRAG_MIME)
            )
              return;
            event.preventDefault();
            setDragOverDestinationId("");
            void documentMove.moveSelectionToDestination(item.moveDestination);
          }}
          aria-current={isActive ? "true" : undefined}
          aria-disabled={isMoveTargetDisabled || undefined}
          disabled={isMoveTargetDisabled}
          title={isMoveTargetDisabled ? (moveValidation.message || (isNonTargetDuringMoveSelection ? "Not a move destination" : item.label)) : item.label}
          style={{
            marginLeft: `${indent}px`,
            marginTop: topLevelSpacing,
            width: `calc(100% - ${indent}px)`,
          }}
        >
          <span className="submission-outline-label" title={item.label}>
            {item.label}
          </span>
          <span className="submission-outline-meta">
            {item.hasNew ? <span className="submission-outline-new-flag">New</span> : null}
            {showStatus && item.status !== "NA" ? (
              <span
                className="submission-outline-icon"
                aria-label={item.status}
                title={item.status}
              >
                {getStatusIcon(item.status)}
              </span>
            ) : null}
          </span>
          {isCelebrating ? (
            <span className="submission-outline-truck" aria-hidden="true">
              <span className="submission-outline-truck-cab">
                <svg viewBox="0 0 64 44" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="9" width="40" height="25" rx="3" fill="#ffffff" stroke="#2b2b2b" strokeWidth="3" />
                  <path d="M43 16 h12 a3 3 0 0 1 3 3 v15 h-15 z" fill="#ffffff" stroke="#2b2b2b" strokeWidth="3" strokeLinejoin="round" />
                  <rect x="46" y="19" width="9" height="7" rx="1" fill="#2b2b2b" />
                  <circle cx="16" cy="36" r="5" fill="#2b2b2b" />
                  <circle cx="50" cy="36" r="5" fill="#2b2b2b" />
                  <circle cx="16" cy="36" r="1.8" fill="#ffffff" />
                  <circle cx="50" cy="36" r="1.8" fill="#ffffff" />
                  <path d="M24 23 l6 -6 l6 6 v7 h-12 z" fill="#ff4612" />
                  <rect x="8" y="16" width="9" height="2.6" rx="1.3" fill="#ff4612" />
                  <rect x="8" y="21" width="6" height="2.6" rx="1.3" fill="#ff4612" />
                </svg>
              </span>
            </span>
          ) : null}
        </button>
        {item.idTypeTargets?.length && documentMove.isMoveMode ? (
          <div className="submission-outline-id-type-targets" style={{ marginLeft: `${indent + 18}px` }}>
            {item.idTypeTargets.map((target) => {
              const targetValidation = validateDocumentMove(documentMove.activeDocuments, target);
              const isTargetDisabled = !targetValidation.valid;
              return (
                <button
                  key={target.id}
                  type="button"
                  className={`submission-outline-id-type-target ${isTargetDisabled ? "disabled" : ""}`}
                  disabled={isTargetDisabled}
                  title={targetValidation.message || target.label}
                  onClick={() => {
                    void documentMove.moveSelectionToDestination(target);
                  }}
                  onDragOver={(event) => {
                    if (isTargetDisabled) return;
                    event.preventDefault();
                    setDragOverDestinationId(target.id);
                  }}
                  onDragLeave={() => setDragOverDestinationId("")}
                  onDrop={(event) => {
                    if (isTargetDisabled) return;
                    event.preventDefault();
                    setDragOverDestinationId("");
                    void documentMove.moveSelectionToDestination(target);
                  }}
                >
                  {target.label}
                </button>
              );
            })}
          </div>
        ) : null}
        {(item.children || []).map((child) => renderItem(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <aside
      ref={outlineRef}
      className="submission-outline print-hide"
      aria-label="Submission outline"
    >
      <div className="submission-outline-header">
        <button
          type="button"
          className="submission-outline-dashboard-link"
          onClick={() => {
            navigate(dashboardPath);
            window.scrollTo({ top: 0, left: 0 });
          }}
          title="Return to dashboard"
        >
          <ArrowBackIcon fontSize="inherit" />
          <span>Return to dashboard</span>
        </button>
        <div className="submission-outline-title-row">
          <div className="submission-outline-title-group">
            <h3>ID {appId ?? "NA"}</h3>
            {applicationStatus !== "NA" ? (
              <span
                className={`submission-outline-header-status submission-outline-item-${applicationStatus
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                aria-label={applicationStatus}
                title={applicationStatusTooltip || applicationStatus}
              >
                {getStatusIcon(applicationStatus)}
              </span>
            ) : null}
          </div>
        </div>
        <p className="submission-outline-property-name" title={propertyName || ""}>
          {propertyName || "NA"}
        </p>
        <hr className="submission-outline-divider submission-outline-header-divider" />
        <div className="submission-outline-applicant-row">
          <p className="submission-outline-applicant-name" title={applicantName || ""}>
            {applicantName || "NA"}
          </p>
          <button
            type="button"
            className={`submission-outline-toolbar-button submission-outline-trash-button ${
              deletedDocCount > 0 ? "submission-outline-trash-button-active" : ""
            } ${dragOverDestinationId === "trash" ? "drag-over" : ""} ${documentMove.celebrationId === "trash" ? "submission-outline-celebrate" : ""}`}
            onClick={onOpenDeletedDocs || undefined}
            onDragOver={(event) => {
              // Recognize the document drag synchronously via its MIME marker so
              // the trash arms immediately, instead of waiting on the drag-
              // selection state to propagate.
              if (!canUseBulkDocumentActions) return;
              if (!event.dataTransfer.types.includes(DOCUMENT_DRAG_MIME)) return;
              event.preventDefault();
              setDragOverDestinationId("trash");
            }}
            onDragLeave={() => setDragOverDestinationId("")}
            onDrop={(event) => {
              if (!canUseBulkDocumentActions) return;
              if (!event.dataTransfer.types.includes(DOCUMENT_DRAG_MIME)) return;
              event.preventDefault();
              setDragOverDestinationId("");
              // Drag-to-trash deletes immediately, with no confirmation dialog.
              documentMove.requestDeleteSelection({ fromDrag: true });
            }}
            aria-label="Deleted documents"
            title={canUseBulkDocumentActions ? "Deleted documents (drag documents here to delete)" : "Deleted documents"}
          >
            <DeleteOutlineIcon fontSize="small" />
            {deletedDocCount > 0 ? (
              <span className="submission-outline-trash-count">({deletedDocCount})</span>
            ) : null}
          </button>
        </div>
        <p className="submission-outline-applicant-type">
          {applicantType || "Applicant"}
        </p>
        <div className="submission-outline-tab-nav submission-outline-tab-nav-lower">
          <button
            type="button"
            className="submission-outline-toolbar-button"
            onClick={onNavigatePrevTab || undefined}
            aria-label="Previous applicant"
            disabled={!canNavigatePrevTab}
          >
            <ChevronLeftIcon fontSize="small" />
          </button>
          <button
            type="button"
            className="submission-outline-toolbar-button"
            onClick={onNavigateNextTab || undefined}
            aria-label="Next applicant"
            disabled={!canNavigateNextTab}
          >
            <ChevronRightIcon fontSize="small" />
          </button>
        </div>
        <hr className="submission-outline-divider submission-outline-list-divider" />
      </div>
      {loading ? (
        <div className="submission-outline-empty">Loading outline...</div>
      ) : items.length === 0 ? (
        <div className="submission-outline-empty">No reviewable items available.</div>
      ) : (
        <nav className="submission-outline-list">
          {items.map((item, index) => renderItem(item, 0, index))}
        </nav>
      )}
    </aside>
  );
};

export default SubmissionOutlineNavigation;
